import _ from 'lodash'
import { actions } from '@adc/ui-morpheus'
import { submit, startSubmit, stopSubmit } from 'redux-form'
import {
  getVenues,
  bulkUpdateVenue,
  getVenuesSuggestions,
} from '../providers/venues'
import { savedSearchProviders } from '../providers/savedSearches'
import {
  CLEAR_VENUES,
  UPDATE_VENUE_IN_LIST,
  UPDATE_BULK_UPDATED_VENUE_COUNT,
  CLEAR_BULK_UPDATED_VENUE_COUNT,
} from '../constants/actionTypes'
import { uiToApiBulkFields } from '../translations/venues'
import { VENUES } from '../constants/tableTypes'
import { FORM_VENUE_BULK_UPDATE } from '../constants/formNames'
import { POST_BULK_UPDATE_DELAY } from '../constants/statics'
import { tableConfig } from '../reducers/venues/columnDefinitions'
import { timeout } from '../util/common'
import { getSuggestionFetcherForField } from '../suggestors'

const getGridVenues = (req, fetchOptions) => {
  const fields = [
    'venue_id',
    'venue_address.street_number',
    'venue_address.street_name',
    'venue_address.country_secondary_subdivision',
    ...tableConfig.fieldDefinitions
      .filter(fieldDefinition => !fieldDefinition.isUiOnly)
      .map(field => field.apiRef),
  ]

  return getVenues(req, {
    ...fetchOptions,
    fields,
  })
}

const suggestionFetcher = async (...args) => {
  const [, { text, fields }] = args
  const field = fields[0]
  const fieldFetcher = getSuggestionFetcherForField(field, tableConfig)

  if (!fieldFetcher) {
    return getVenuesSuggestions(...args)
  }

  const values = await fieldFetcher(text)

  return [{ field, values }]
}

export const {
  fetchTable,
  fetchTableAppend,
  fetchTableBatches,
  addFilter,
  clearFilters,
  changeSortBy,
  removeFilter,
  savedSearchActions,
} = actions.dataTable.initDataTable(VENUES, {
  getTableValues: getGridVenues,
  ...savedSearchProviders,
})

export const { abortTableValues, resetTableValues } = actions.dataTable

export const updateSuggestions = actions.dataTable.updateSuggestions(
  suggestionFetcher,
  VENUES
)

export const updateVenueInList = venue => ({
  type: UPDATE_VENUE_IN_LIST,
  venue,
})

export const clearVenues = () => ({
  type: CLEAR_VENUES,
})

export const updateBulkUpdatedVenueCount = (venuesUpdated, venueCount) => ({
  type: UPDATE_BULK_UPDATED_VENUE_COUNT,
  venuesUpdated,
  venueCount,
})

export const clearBulkUpdatedVenueCount = () => ({
  type: CLEAR_BULK_UPDATED_VENUE_COUNT,
})

// This action creator doesn't have a corresponding reducer because we don't want
// to store anything in redux and all the reduxy things happen within redux-form
export const bulkUpdateVenues = (
  venueIds,
  postUpdateDelay = POST_BULK_UPDATE_DELAY
) => {
  return async (dispatch, getState) => {
    const formValues = _.get(
      getState(),
      `form.${FORM_VENUE_BULK_UPDATE}.values`
    )

    // Submit via redux-form to force any validation errors first
    dispatch(submit(FORM_VENUE_BULK_UPDATE))
    // Turn spinner back on while bulk updating since form submission stops spinner animation
    dispatch(startSubmit(FORM_VENUE_BULK_UPDATE))

    // Split into arrays of 100 venue ids
    const batches = _.chunk(venueIds, 100)

    const promises = await Promise.all(
      batches.map(async batch => {
        try {
          // to show some progress in the beginning
          dispatch(
            updateBulkUpdatedVenueCount(
              Math.floor(batch.length / 2),
              venueIds.length
            )
          )
          const response = await bulkUpdateVenue(null, {
            venue_ids: batch,
            update: { ...uiToApiBulkFields(formValues) },
          })
          // show other "half" of progress being completed
          dispatch(
            updateBulkUpdatedVenueCount(
              Math.ceil(response.success.length / 2),
              venueIds.length
            )
          )
          await timeout(postUpdateDelay)
          return response
        } catch (error) {
          dispatch(stopSubmit(FORM_VENUE_BULK_UPDATE))
          return error
        }
      })
    )

    dispatch(stopSubmit(FORM_VENUE_BULK_UPDATE))
    dispatch(clearBulkUpdatedVenueCount())

    const customizer = (objValue, srcValue) => {
      if (_.isArray(objValue)) {
        return objValue.concat(srcValue)
      }
    }

    const promiseResponse = _.mergeWith(...promises, customizer)

    return promiseResponse
  }
}
